import * as React from 'react';
import { ReactElement, useEffect, useState } from 'react';

import { post } from '../../../api/utils';
import ErrorInline from '../../common/ErrorInline';
import Icon from '../../common/Icon';
import SearchAndSelect from '../../search/SearchAndSelect';
import { ISearchResult } from '../../search/SearchBar';

interface IProps {
    csrfToken: string;
    requestTeamPartnerURI: string;
    searchURI: string;
    teamSize: number;
}

export default function RequestTeamMember({
    csrfToken,
    requestTeamPartnerURI,
    searchURI,
    teamSize,
}: IProps): ReactElement {
    const [readyToSubmit, setReadyToSubmit] = useState<boolean>(false);
    const [error, setError] = useState<string | null>(null);
    const [loading, setLoading] = useState<boolean>(false);
    const [partners, setPartners] = useState<Array<ISearchResult | null>>(
        Array(teamSize - 1).fill(null),
    );

    const onSubmitPartners = async (): Promise<void> => {
        if (loading || !readyToSubmit) {
            return;
        }

        setLoading(true);
        try {
            await post(requestTeamPartnerURI, csrfToken, {
                /* eslint-disable @typescript-eslint/camelcase */
                partner_ids: partners.map((p) => p.id),
                /* eslint-enable @typescript-eslint/camelcase */
            });
            window.location.reload();
        } catch (err) {
            setError(err.message);
            setLoading(false);
        }
    };

    const onSelect = (item: ISearchResult | null, index: number): void => {
        setPartners((v) => {
            const newPartners = [...v];
            newPartners.splice(index, 1, item);
            return newPartners;
        });
    };

    useEffect(() => {
        setError(null);
        if (partners.some((p) => p === null)) {
            setReadyToSubmit(false);
            return;
        }

        let ready = true;
        if (new Set(partners.map((p) => p.id)).size !== partners.length) {
            setError('You cannot select the same partner twice');
            ready = false;
        }
        setReadyToSubmit(ready);
    }, [partners]);

    return (
        <>
            <div className="list-group">
                {partners.map((partner, i) => (
                    <div
                        className="list-group-item"
                        key={`${partner?.id}-${i}`}
                    >
                        <SearchAndSelect
                            csrfToken={csrfToken}
                            model="users"
                            onClear={async (): Promise<void> => {
                                onSelect(null, i);
                            }}
                            onSelect={async (
                                item: ISearchResult,
                            ): Promise<void> => {
                                onSelect(item, i);
                            }}
                            placeholder="Search for a partner"
                            searchURI={searchURI}
                            selectedItem={partner}
                        />
                    </div>
                ))}
            </div>

            <div className="align-items-center d-flex flex-row-reverse flex-wrap gap-3">
                <button
                    className="btn btn-primary"
                    disabled={loading || !readyToSubmit}
                    onClick={onSubmitPartners}
                >
                    <Icon
                        fixedWidth
                        name={loading ? 'circle-o-notch' : 'paper-plane'}
                        spin={loading}
                        text="Send the request"
                    />
                </button>

                {error && <ErrorInline className="mb-0" error={error} />}
            </div>
        </>
    );
}
